import { Injectable } from '@angular/core';
import {
  MessagePosition,
  MessageType,
  SnackBarService,
} from '@epicuro-next/ataraxia/snack-bar';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import { LoggerService } from '@epicuro-next/platform/monitoring';
import {
  loadList,
  loadNextListProducts,
  QUERY_UPDATE_STRATEGY,
  resetListProducts,
} from '@epicuro-next/state/list-entities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { loadProductList, saveAllItemsInListSuccess } from '../../actions';
import * as actions from '../../actions';
import { selectActiveListId } from '../../selectors/lists/product-list-active.selectors';
import { selectProductListPatchCompleteQuery } from '../../selectors/lists/product-lists.selectors';

/**
 * Save all items effects
 */
@Injectable()
export class SaveAllItemsEffects {
  public saveAllItemsInActiveListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.saveAllItemsInListSuccess),
      withLatestFrom(this.store.pipe(select(selectActiveListId))),
      filter(([_, activeListId]) => !!activeListId),
      tap(() => {
        const message = $localize`:productBasket|Search result items will be added text@@savedAllItemsInActiveListMessage:Search result items will be added to your list soon`;
        this.snackBarService.show(
          message,
          MessagePosition.BOTTOM,
          MessageType.DANGER,
          $localize`:productBasket|Added product close message@@addedProductCloseMessage:Close`,
        );
      }),
      map(([_, activeListId]) =>
        actions.loadProductListPolling({
          id: activeListId,
        }),
      ),
    ),
  );

  /**
   * Effect to load the ProductLists
   */
  public saveAllItemsInList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.saveAllItemsInList),
      withLatestFrom(
        this.store.pipe(select(selectProductListPatchCompleteQuery)),
      ),
      switchMap(([action, query]) =>
        this.productListService
          .bulkAddProducts({ listId: action.list?.id ?? '', query })
          .pipe(
            map(() =>
              actions.saveAllItemsInListSuccess({
                id: action.list.id,
              }),
            ),
            catchError(() => of(actions.saveAllItemsInListError())),
          ),
      ),
    ),
  );

  // copied from bulk add effect
  public pushNewListProductsToListEntity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveAllItemsInListSuccess),
      concatMap(({ id }) => [
        loadProductList({ id }),
        resetListProducts({ listId: id }),
        loadList({ listId: id }),
        loadNextListProducts({
          listId: id,
          strategy: QUERY_UPDATE_STRATEGY.OVERWRITE,
        }),
      ]),
    ),
  );

  /**
   * Effect to signal failure patching a product list
   */
  public saveAllItemsInListError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.saveAllItemsInListError),
        tap(() => {
          this.loggerService.exception({
            error: new Error(
              'Error during bulk adding of products in a product list',
            ),
          });
        }),
      ),
    {
      dispatch: false,
    },
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private productListService: ProductListApiService,
    private snackBarService: SnackBarService,
    private loggerService: LoggerService,
  ) {}
}
